<script setup>
/**
 * Your picture (XN-4561): the person as the hub draws them — their photo, or
 * their initials on a colour they choose — and who sees it. A colour saves
 * the moment it is pressed; a photo is cropped square first. With a photo set
 * the colour still chooses what shows if the photo goes: the photo simply
 * covers it, as in the prototype. Prototype `profile()` and `PAGES.profile`,
 * build 30 Sep 12:40.
 */
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue';
import XlAvatar from '../xl/XlAvatar.vue';
import XlButton from '../xl/XlButton.vue';
import XlTag from '../xl/XlTag.vue';
import IconCheck from '../icons/IconCheck.vue';
import IconImage from '../icons/IconImage.vue';
import PortalDetailsPhotoDialog from './PortalDetailsPhotoDialog.vue';
import { removeMyPhoto, updateMyDetails } from '../../api/portal';
import { AVATAR_COLORS } from '../../helpers/avatarColors';

const SAVED_MS = 2200;
const NOTES = {
  colour: "Couldn't save your color.",
  photo: "Couldn't save your photo — try again.",
};
const note = ref(''); // the server's own reason for a refused removal, when it gave one

const props = defineProps({
  me: { type: Object, required: true },
  hub: { type: String, required: true },
  hasBroker: { type: Boolean, default: false },
});
// saved: the whole page, as a colour save answers it. photo: the new photo's URL, or null.
const emit = defineEmits(['saved', 'photo']);

const name = computed(() => [props.me.first_name, props.me.last_name].filter(Boolean).join(' '));
const seenBy = computed(() => (props.hasBroker
  ? `Your broker, ${props.hub} and people in sessions you join can see this.`
  : `${props.hub} and people in sessions you join can see this.`));

// The ring moves at once; a refused save puts it back on what the server holds.
const chosen = ref(props.me.avatar_color ?? null);
const busy = ref(false);
// A colour saved elsewhere on the page (or answered late) moves the ring too.
watch(() => props.me.avatar_color, (c) => { if (!busy.value) chosen.value = c ?? null; });
const status = ref(''); // '' | saved | colour | photo
let savedTimer = null;
function say(next) {
  clearTimeout(savedTimer);
  status.value = next;
  if (next === 'saved') savedTimer = setTimeout(() => { status.value = ''; }, SAVED_MS);
}

// One save at a time: a second colour pressed mid-save is ignored, not raced.
async function pickColour(key) {
  if (busy.value || key === chosen.value) return;
  busy.value = true;
  chosen.value = key;
  try {
    emit('saved', await updateMyDetails({ avatar_color: key }));
    say('saved');
  } catch {
    chosen.value = props.me.avatar_color ?? null;
    say('colour');
  } finally {
    busy.value = false;
  }
}

const picker = ref(null);
const uploadButton = ref(null);
const cropSrc = ref(null);

// A data: URL, not a blob: one: the cropper reads its source with XHR, and every
// hub's CSP (connect-src, from maestro's nginx) refuses blob: URLs.
const dataUrlOf = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = () => reject(reader.error);
  reader.readAsDataURL(file);
});

let picks = 0;

async function onPicked(event) {
  const file = event.target.files?.[0];
  event.target.value = '';
  if (!file) return;
  const pick = ++picks; // two quick picks: the last one wins
  try {
    const src = await dataUrlOf(file);
    if (pick === picks) cropSrc.value = src;
  } catch {
    say('photo');
  }
}

function closeCrop() {
  cropSrc.value = null;
}

function onPhotoSaved(url) {
  closeCrop();
  emit('photo', url);
  say('saved');
}

async function removePhoto() {
  if (busy.value) return;
  busy.value = true;
  try {
    await removeMyPhoto();
    emit('photo', null);
    say('saved');
    // The button pressed is gone; the focus goes to the one that puts a photo back.
    await nextTick();
    uploadButton.value?.$el?.focus();
  } catch (error) {
    // A 422 carries the server's reason (e.g. the last name SSO needs).
    note.value = error?.response?.status === 422 ? error.response.data?.message || '' : '';
    say('photo');
  } finally {
    busy.value = false;
  }
}

onBeforeUnmount(() => {
  clearTimeout(savedTimer);
});
</script>

<template>
  <section class="pdet-pic" data-testid="details-card-picture">
    <XlAvatar :name="name" :src="me.photo || ''" :color="chosen || ''" :size="78" />

    <div class="pdet-pic__t">
      <b>Your picture</b>
      <p>{{ seenBy }}</p>
      <div class="pdet-pic__tools">
        <button
          v-for="colour in AVATAR_COLORS"
          :key="colour.key"
          type="button"
          class="pdet-pic__sw"
          :style="{ background: colour.hex }"
          :aria-label="`Use ${colour.name}`"
          :aria-pressed="colour.key === chosen ? 'true' : 'false'"
          :aria-disabled="busy ? 'true' : undefined"
          @click="pickColour(colour.key)"
        />
        <XlButton ref="uploadButton" variant="ghost" size="sm" @click="picker.click()">
          <template #icon><IconImage color="currentColor" size="14" /></template>
          Upload a photo
        </XlButton>
        <XlButton v-if="me.photo" variant="ghost" size="sm" @click="removePhoto">Remove photo</XlButton>
        <span class="pdet-pic__status" role="status" data-testid="details-picture-status">
          <XlTag v-if="status === 'saved'" tone="success"><IconCheck color="currentColor" size="12" /> Saved</XlTag>
          <template v-else>{{ (status === 'photo' && note) || NOTES[status] || '' }}</template>
        </span>
      </div>
      <input
        ref="picker"
        type="file"
        accept="image/jpeg,image/png,image/webp"
        class="pdet-pic__picker"
        tabindex="-1"
        aria-label="Choose a photo"
        @change="onPicked"
      />
    </div>

    <PortalDetailsPhotoDialog v-if="cropSrc" :src="cropSrc" @close="closeCrop" @saved="onPhotoSaved" />
  </section>
</template>

<style scoped>
/* Measurements from the prototype's profile() picture card, build 30 Sep 12:40. */
.pdet-pic {
  position: relative;
  display: flex;
  gap: 20px;
  align-items: center;
  flex-wrap: wrap;
  padding: 22px 24px;
  margin: 0 0 14px;
  background: #fff;
  border: 1px solid var(--xl-color-border);
  border-radius: var(--xl-radius-lg);
  box-shadow: var(--xl-shadow-1);
}
.pdet-pic__t { flex: 1; min-width: 200px; }
.pdet-pic__t b { font-size: 16px; }
.pdet-pic__t p { margin: 3px 0 12px; font-size: 13.5px; color: var(--xl-color-outline); }
.pdet-pic__tools { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; }
.pdet-pic__sw {
  width: 30px;
  height: 30px;
  flex: 0 0 30px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  cursor: pointer;
}
.pdet-pic__sw[aria-pressed="true"] { outline: 2.5px solid var(--xl-color-on-surface); outline-offset: 2px; }
.pdet-pic__sw:focus-visible { outline: 2.5px solid var(--xl-color-primary); outline-offset: 2px; }
.pdet-pic__status { font-size: 13.5px; color: var(--xl-color-error); }
.pdet-pic__picker { position: absolute; width: 1px; height: 1px; opacity: 0; pointer-events: none; }
@media (max-width: 480px) { .pdet-pic { padding: 18px; } }
</style>
